/**
 * Site check-in (piece 13, decision 0229). 🔨 A check-in with no check-out is
 * never closed silently: the daily job stamps `autoClosedAt?` and leaves
 * `checkedOutAt?` null, because no departure was observed and a made-up one
 * would put an invented duration on a Work Order.
 */
import { calendarDayInZone } from '../zonedTime';

export const CHECK_IN_SOURCES = ['Mobile', 'Manual'] as const;
export type CheckInSource = (typeof CHECK_IN_SOURCES)[number];

/** The retention the nightly pass reads before it nulls the coordinates. */
export const COORDINATE_RETENTION_KEY = 'checkin.coordinateRetentionDays';
export const DEFAULT_COORDINATE_RETENTION_DAYS = 90;

/**
 * Past this many metres from its site a check-in reads in doubt (piece 13,
 * settled 2026-09-14; piece 15 exposes it in Settings › Security). 🔨 It ships
 * empty, so no visit is called into doubt until the product owner sets a
 * distance.
 */
export const COORDINATE_TOLERANCE_KEY = 'checkin.coordinateToleranceMeters';

/** The tolerance a Settings value names, or none: a blank, a nought or anything but a number above zero is no tolerance. */
export function coordinateToleranceMeters(held: unknown): number | null {
  return typeof held === 'number' && Number.isFinite(held) && held > 0 ? held : null;
}

/** What Settings refuses for the tolerance; a blank passes, because a blank clears it. */
export function coordinateToleranceProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return coordinateToleranceMeters(value) === null ? 'the check-in distance tolerance is a number of meters above zero, or blank for none' : null;
}

/** The distance along the ground between two points, in metres. */
export function metersBetween(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** How far a check-in fell from its site, and whether that is past the tolerance. */
export interface CheckInDistance {
  meters: number;
  /** True only where a tolerance is set and the distance is past it. */
  inDoubt: boolean;
}

/**
 * 🔒 The distance a check-in fell from its site, derived on read and never
 * stored (piece 13, settled 2026-09-14). The site's point comes from the one
 * precedence rule (decision 0674). 🔨 A row whose check-in or whose site carries
 * no point shows no distance rather than a wrong one.
 */
export function checkInDistance(
  checkIn: { latitude?: number | null; longitude?: number | null },
  site: { latitude: number; longitude: number } | null,
  toleranceMeters: number | null,
): CheckInDistance | null {
  if (!site || typeof checkIn.latitude !== 'number' || typeof checkIn.longitude !== 'number') return null;
  const meters = metersBetween({ latitude: checkIn.latitude, longitude: checkIn.longitude }, site);
  return { meters, inDoubt: toleranceMeters !== null && meters > toleranceMeters };
}

/** A distance as a person reads it: whole metres under a kilometre, a kilometre to one decimal past it. */
export function formatMeters(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

export class CheckInError extends Error {}

export interface CheckInRow {
  id?: string;
  employmentId: string;
  siteId: string;
  checkedInAt: Date;
  checkedOutAt?: Date | null;
  autoClosedAt?: Date | null;
  source?: CheckInSource;
  eventId?: string | null;
  workOrderId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export function checkInProblems(row: CheckInRow): string[] {
  const problems: string[] = [];
  if (row.checkedOutAt && row.checkedOutAt.getTime() < row.checkedInAt.getTime()) problems.push('a departure cannot precede the arrival');
  if (row.checkedOutAt && row.autoClosedAt) problems.push('a visit is closed by a person or by the daily job, never by both');
  if ((row.latitude === null || row.latitude === undefined) !== (row.longitude === null || row.longitude === undefined)) {
    problems.push('coordinates arrive together or not at all');
  }
  return problems;
}

/** A visit is open while it has neither a departure nor an automatic close. */
export function isOpen(row: CheckInRow): boolean {
  return !row.checkedOutAt && !row.autoClosedAt;
}

/**
 * Midnight is read in the Organization's own time zone, so a visit still open
 * past it is closed by the daily pass. The job reports that it ran even with
 * nothing due.
 */
export function dueForAutoClose(rows: readonly CheckInRow[], now: Date, timeZone: string): CheckInRow[] {
  const today = calendarDayInZone(now, timeZone);
  return rows.filter((row) => isOpen(row) && calendarDayInZone(row.checkedInAt, timeZone) !== today);
}

/**
 * 🔨 The day's close prompts through a `ChecklistItem`, never a surface this
 * module invents. One Open item per visit closed, assigned to that check-in's
 * own person, naming the site and the arrival time and asking for the departure.
 */
export interface AutoCloseItem {
  checkInId: string;
  employmentId: string;
  siteId: string;
  title: string;
}

export function autoClosePrompt(row: CheckInRow, siteName: string): AutoCloseItem {
  return {
    checkInId: row.id ?? '',
    employmentId: row.employmentId,
    siteId: row.siteId,
    title: `What time did you leave ${siteName}? You arrived at ${row.checkedInAt.toISOString().slice(11, 16)} and no departure was recorded.`,
  };
}

/** The coordinates age out; the visit, its times and its site are kept. */
export function coordinatesExpired(row: CheckInRow, now: Date, retentionDays = DEFAULT_COORDINATE_RETENTION_DAYS): boolean {
  return now.getTime() - row.checkedInAt.getTime() > retentionDays * 24 * 60 * 60 * 1000;
}

/**
 * 🔒 The Work Order page's on-site panel (piece 29, T309; decision 0873): each
 * assigned worker — the distinct employments on the order's line rows — with
 * the visit they hold open, if any, and whether the viewer is that worker. At a
 * gated site the page asks for the gate device and the worker's clock PIN.
 */
export interface WorkOrderOnSiteWorker {
  employmentId: string;
  personId: string;
  name: string;
  /** True where the viewer checks in their own employment, the one case the browser's location is sent. */
  isViewer: boolean;
  /** The visit this worker holds open anywhere; the one-open-visit rule reads it. */
  open: { checkInId: string; siteName: string; here: boolean; checkedInAt: string; hasLocation: boolean } | null;
}

export interface WorkOrderOnSiteAnswer {
  workOrderId: string;
  siteName: string;
  gated: boolean;
  /** The gate devices registered at the order's site, offered on a gated site alone. */
  devices: { id: string; label: string }[];
  workers: WorkOrderOnSiteWorker[];
}

/** What Check in and Check out send (T309). The point is sent only for the viewer's own employment; the device and PIN only at a gated site. */
export interface WorkOrderOnSiteRequest {
  employmentId: string;
  latitude?: number | null;
  longitude?: number | null;
  deviceId?: string | null;
  secret?: string | null;
}

